import { registerApplication, start } from 'single-spa';

import * as topbar from './applications/topbar';
import * as sidebar from './applications/sidebar';
import * as inspections from './applications/inspections';
import * as flexDSM from './applications/flexDSM';
import * as formFolders from './applications/formFolders';
import * as siteManager from './applications/siteManager';
import * as opsDashboard from './applications/opsDashboard';
import * as wxPortal from './applications/wxPortal';
import * as middleware from './utilities/middleware';
import { getFeatureFlags, isFeatureFlagEnabled, getLocales} from './utilities/api';

// we have some custom domains, we need to know matching app-slugs for those
const customHostnames = {
    // since we can't test this easily with the real domains until on prod, we can test this locally
    // uncomment the :9000 address here, load it up, check the Network tab that the correct
    // JSON file is being loaded by orchestrator (it will not load the other app, just some of its
    // metadata): You will see the bell-my-receipts overrides scripts in the HTML elements.
    // 'welcome.weeverdev.com:9000': 'bell-my-receipts',
    'niagara-san': 'niagara',
    'fuelreceipts.bell.ca': 'bell-fuel-receipts',
    'recusdecarburant.bell.ca': 'bell-fuel-receipts',
    'myreceipts.bell.ca': 'bell-my-receipts',
    'husqvarna.jergensapp.com': 'jergens-husqvarna',
    'www.fuelreceipts.bell.ca': 'bell-fuel-receipts',
    'www.recusdecarburant.bell.ca': 'bell-fuel-receipts',
    'www.myreceipts.bell.ca': 'bell-my-receipts',
    'www.husqvarna.jergensapp.com': 'jergens-husqvarna',
}; /* Domain mapping support */

const APP_SLUG =
    customHostnames[window.location.host] ||
    customHostnames[window.location.host.split('.')[0]] ||
    window.location.host.split('.')[0];
const IS_LOCAL = window.location.port > 443;
const APP_ROOT_URL = IS_LOCAL ? `http://localhost:8500/wxportal` : '/wxportal';

registerApplication({
    name: 'inspections',
    app: () => inspections.load(IS_LOCAL),
    activeWhen: ['/inspections/'],
});

getLocales(APP_SLUG).then((localesFromDatabase) => {
    registerApplication({
        name: '@weever/wxPortal',
        app: () => wxPortal.load(APP_ROOT_URL, APP_SLUG, localesFromDatabase),
        activeWhen: [
            (it) =>
                !it.pathname.startsWith('/site-manager') &&
                !it.pathname.startsWith('/inspections') &&
                !it.pathname.startsWith('/databases') &&
                !it.pathname.startsWith('/form-folders') &&
                !it.pathname.startsWith('/dashboard'),
        ],
    });

    registerApplication({
        name: '@weever/siteManager',
        app: () => siteManager.load(IS_LOCAL),
        activeWhen: [
            (it) =>
                it.pathname.startsWith('/site-manager')
        ],
    });

    isFeatureFlagEnabled('microMenus', APP_SLUG)
        .then((isMicroMenusEnabled) => {
            registerApplication({
                name: 'topbar',
                app: topbar,
                activeWhen: () => isMicroMenusEnabled,
                customProps: {
                    appSlug: APP_SLUG,
                    appRootUrl: APP_ROOT_URL,
                    localesFromDatabase: localesFromDatabase
                },
            });
            registerApplication({
                name: 'sidebar',
                app: sidebar,
                activeWhen: () => isMicroMenusEnabled,
                customProps: {
                    appSlug: APP_SLUG,
                    appRootUrl: APP_ROOT_URL,
                    localesFromDatabase: localesFromDatabase
                },
            });

            middleware.load(APP_SLUG);

            return APP_SLUG;
        })
        .then(middleware.loadAsync)
        .then(() => getFeatureFlags(APP_SLUG))
        .then((featureFlags) => {
            const isFlexDSMEnabled = featureFlags.includes('flexDSM')
                || featureFlags.includes('flexDSMLimited');

            if (isFlexDSMEnabled) {
                registerApplication({
                    name: '@weever/flexDSM',
                    app: () => flexDSM.load(IS_LOCAL),
                    activeWhen: ['/databases/', '/databases'],
                });
            }
        })
        .then(() => isFeatureFlagEnabled('formFoldersNext', APP_SLUG))
        .then((isFormFoldersNextEnabled) => {
            if (isFormFoldersNextEnabled) {
              registerApplication({
                  name: '@weever/formFolders',
                  app: () => formFolders.load(IS_LOCAL),
                  activeWhen: ['/form-folders'],
              });
            }
        })
        .then(() => isFeatureFlagEnabled('opsDashboard', APP_SLUG))
        .then((isOpsDashboardEnabled) => {
            if (isOpsDashboardEnabled) {
                registerApplication({
                    name: '@weever/opsDashboard',
                    app: () => opsDashboard.load(IS_LOCAL),
                    activeWhen: ['/dashboard/', '/dashboard'],
                });
            }
        })
        .finally(start);
});